import React, { useEffect } from "react";
import AceEditor from "react-ace";
import clsx from "clsx";

import { makeStyles, Theme } from "@material-ui/core";

import useSetState from "../../hooks/useSetState";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    marginTop: 16,
  },
  header: {
    padding: 0,
    margin: "0 0 4px 0",
  },
  error: {
    fontSize: 12,
    color: theme.palette.error.main,
  },
}));

interface JSONEditorProps {
  value?: unknown;
  jsonChanged?: ((json: "") => void) | null;
  className?: string;
  customTitle?: JSX.Element;
  titleText?: string;
  width?: string;
}

const JSONEditor: React.FC<JSONEditorProps> = ({
  value,
  jsonChanged = null,
  className,
  customTitle,
  width = "95%",
  titleText,
}: JSONEditorProps) => {
  const classes = useStyles();
  const [{ json, errorMessage }, setState] = useSetState({
    json: "",
    errorMessage: "",
  });

  const handleChange = (v) => {
    let errorM = null;
    try {
      JSON.parse(v);
    } catch (e) {
      errorM = e.message;
    }
    setState({
      json: v,
      errorMessage: errorM,
    });
    if (jsonChanged) {
      jsonChanged(v);
    }
  };

  useEffect(() => {
    if (value) {
      setState({ json: JSON.stringify(value, null, "\t") });
    }
  }, [value]);

  const title = <p className={classes.header}>{titleText}</p>;

  return (
    <div className={clsx(classes.container, className)}>
      {customTitle || title}
      <AceEditor
        mode="json"
        width={width}
        height="600px"
        theme="monokai"
        name="jsonEditor"
        onChange={handleChange}
        fontSize={14}
        showPrintMargin={true}
        showGutter={true}
        highlightActiveLine={true}
        value={json}
        setOptions={{
          enableBasicAutocompletion: false,
          enableLiveAutocompletion: false,
          enableSnippets: false,
          showLineNumbers: true,
          tabSize: 2,
          useWorker: false,
        }}
        style={{ borderRadius: 4 }}
      />
      {errorMessage && (
        <p className={classes.error}>{`Validation error: ${errorMessage}`}</p>
      )}
    </div>
  );
};

export default JSONEditor;
